import React, { useCallback, useMemo } from 'react';
import { sortFundNames } from '../../../../utilities/customSorting';
import { useSelector } from 'react-redux';
import { clientNameSelector } from '../../../../redux/auth/selectors';
import { raptorPlatformIdSelector } from '../../../../redux/raptorPlatform/selectors';
import { createSectionByIdSelector } from '../../../../redux/pages/selectors';
import { createGeneralTableData } from './overview-columns';
import NoDataMessage from '../../../feedback/NoDataMessage.component';
import { GeneralOverviewPageRenderedComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { useAllFundNotes } from '../../../../hooks/useFundNotes';
import { Note } from '../../../../sharedInterfaces/notes';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { RSOverviewPageModel } from '../../../../api/api.types';
import type { RiskOverviewMonitorFundRow } from '../../../../api/rs-hg/risk/riskOverview.types';
import {
  ReportsListByFund,
  useReportsListQuery,
  useRiskOverviewMonitorQuery,
} from '../../../../api/rs-hg/risk/hooks/queries';
import {
  addSupplementaryRulesColumn,
  getOverviewColumnsFields,
} from './overviewColumnFields';
import OverviewTableV2 from './OverviewTableV2';
import OverviewSecondLevelGraphs from './OverviewSecondLevelGraphs.component';
import { mapStatusToColor } from '../../../../utilities/colorUtilities';
import { specifyStatus } from '../../../../utilities/generalMappings';
import { Status } from '../../../../types/redux/data/dataTypes';
import {
  getOverviewColumnsV2,
  overviewInitialColumnVisibility,
} from './overviewColumnsV2';

type FundType = 'var' | 'commitment' | 'both';

export interface GeneralOverviewTableData {
  internalFundName: string;
  fundName: string;
  reports: string[];
  subFundRiskReports: string[];
  positionDate: string;
  calculationDate: string;
  currency: string;
  netAssetValue: number;
  commitmentValue: number;
  commitmentStatus: Status;
  valueAtRiskStatus: Status;
  valueAtRiskValue: number;
  varFund: string;
  varDay: string;
  ucitsLaw: Status;
  prospectusStatus: Status;
  countryExposure: any;
  positionExposure: any;
  index: number;
  derogationMessage?: string;
  timeSeriesData: any | null;
  timeSeriesStatus: any | null;
  notes: boolean;
  leverage: Status;
  fundType: FundType;
  act40Law?: Status;
  riaifLaw?: Status;
  qiaifLaw?: Status;
  nursLaw?: Status;
  fivgLaw?: Status;
  partiiLaw?: Status;
  cssfRiskSpreading?: Status;
  supplementaryRules?: Status;
}

type OverviewPageData = {
  overview: RSOverviewPageModel<RiskOverviewMonitorFundRow[]>;
  reports: ReportsListByFund;
  subFundRiskReports: ReportsListByFund;
};

type OverviewTableSectionProps = {
  overview: RSOverviewPageModel<RiskOverviewMonitorFundRow[]>;
  reports: ReportsListByFund;
  subFundRiskReports: ReportsListByFund;
  clientName: string | null;
  section: string;
  sectionId?: string;
  sectionTitle: string;
  positionDate?: string;
  getNotesForFund: (fundName: string, positionDate: string) => Note[];
  preFetchedNotesLoading: boolean;
};

const OverviewTableSection: React.FC<OverviewTableSectionProps> = ({
  overview,
  reports,
  subFundRiskReports,
  clientName,
  section,
  sectionId,
  sectionTitle,
  positionDate,
  getNotesForFund,
  preFetchedNotesLoading,
}) => {
  const columnsFields = useMemo(
    () =>
      addSupplementaryRulesColumn(
        getOverviewColumnsFields(clientName, sectionId),
        overview.data,
      ),
    [clientName, sectionId, overview.data],
  );

  const dataPreparedForTable = useMemo(
    () =>
      createGeneralTableData(
        overview.data,
        reports,
        subFundRiskReports,
        clientName,
        section,
      ),
    [overview.data, reports, subFundRiskReports, clientName, section],
  );

  const columns = useMemo(
    () =>
      columnsFields
        ? getOverviewColumnsV2(
            clientName,
            columnsFields,
            section,
            getNotesForFund,
            preFetchedNotesLoading,
          )
        : [],
    [
      clientName,
      columnsFields,
      section,
      getNotesForFund,
      preFetchedNotesLoading,
    ],
  );

  const exportFields = useMemo(
    () =>
      (columnsFields ?? []).filter(
        (field) => field !== 'reports' && field !== 'index',
      ),
    [columnsFields],
  );

  const renderDetail = useCallback(
    (row: GeneralOverviewTableData) => (
      <OverviewSecondLevelGraphs
        fundName={row.internalFundName}
        positionDate={row.positionDate}
        varFund={row.varFund === 'yes'}
        passValue={mapStatusToColor(specifyStatus(row.timeSeriesStatus))}
      />
    ),
    [],
  );

  const getRowId = useCallback(
    (row: GeneralOverviewTableData) => row.internalFundName,
    [],
  );

  if (!columnsFields) {
    return (
      <NoDataMessage
        message={`No configuration found for the section ${sectionId ?? section}`}
      />
    );
  }

  return (
    <OverviewTableV2<GeneralOverviewTableData>
      data={dataPreparedForTable}
      columns={columns}
      title={`${sectionTitle} Overview Data`}
      pdfIdentifier={`${section}-overview`}
      positionDate={positionDate}
      exportFields={exportFields}
      initialColumnVisibility={overviewInitialColumnVisibility}
      emptyMessage="No funds found"
      noMatchesMessage="No matches for current search"
      getRowId={getRowId}
      renderDetail={renderDetail}
    />
  );
};

/**
 * TanStack + virtualised overview page.
 */
const OverviewPage: React.FC<GeneralOverviewPageRenderedComponentProps> = (
  props,
) => {
  const sectionDetails = useSelector(createSectionByIdSelector(props.section));
  const fundTypes = sectionDetails?.fundTypes;
  const clientName = useSelector(clientNameSelector);
  const selectedRaptorPlatformId = useSelector(raptorPlatformIdSelector);
  const includeSubFundRiskReports = clientName?.includes('pure') ?? false;

  const reportsListQuery = useReportsListQuery({
    fundTypes,
    raptorPlatformId: selectedRaptorPlatformId,
  });

  const overviewQuery = useRiskOverviewMonitorQuery({
    positionDate: props.positionDate,
    fundTypes,
    raptorPlatformId: selectedRaptorPlatformId,
  });

  const subFundRiskReportsQuery = useReportsListQuery(
    {
      fundTypes,
      raptorPlatformId: selectedRaptorPlatformId,
      reportType: 'sub_fund_risk_report',
    },
    { enabled: includeSubFundRiskReports },
  );

  const reportsReady =
    !reportsListQuery.isLoading &&
    (!includeSubFundRiskReports || !subFundRiskReportsQuery.isLoading);

  const pageData = useMemo((): OverviewPageData | null => {
    if (!overviewQuery.data?.data || !reportsReady) {
      return null;
    }

    const overviewRows = [...overviewQuery.data.data].sort((a, b) =>
      sortFundNames(a.fund_name || '', b.fund_name || ''),
    );

    return {
      overview: {
        ...overviewQuery.data,
        data: overviewRows,
      },
      reports: reportsListQuery.data ?? {},
      subFundRiskReports: subFundRiskReportsQuery.data ?? {},
    };
  }, [
    overviewQuery.data,
    reportsListQuery.data,
    subFundRiskReportsQuery.data,
    reportsReady,
  ]);

  const overviewRows = overviewQuery.data?.data ?? [];

  const fundNames = overviewRows.map((fund) => fund.fund_name);

  const positionDates = overviewRows
    .map((fund) => fund.monitor_data?.selected_position_date)
    .filter((d): d is string => !!d);

  const sortedDates = [...positionDates].sort();
  const earliestDate = sortedDates[0];
  const latestDate = sortedDates[sortedDates.length - 1];

  const { fundNotesData, loading: preFetchedNotesLoading } = useAllFundNotes({
    fundNames,
    date1: earliestDate || '',
    date2: latestDate,
    subject: undefined,
    topic: undefined,
    shouldCall: !!earliestDate && fundNames.length > 0,
  });

  const notesByFundAndDate = useMemo(() => {
    const map = new Map<string, Note[]>();
    Object.entries(fundNotesData).forEach(([fundName, notes]) => {
      notes.forEach((note: Note) => {
        const key = `${fundName}-${note.fund_note_position_date}`;
        const existing = map.get(key) || [];
        existing.push(note);
        map.set(key, existing);
      });
    });
    return map;
  }, [fundNotesData]);

  const getNotesForFund = useCallback(
    (fundName: string, positionDate: string): Note[] => {
      const key = `${fundName}-${positionDate}`;
      return notesByFundAndDate.get(key) || [];
    },
    [notesByFundAndDate],
  );

  const isLoading =
    overviewQuery.isLoading ||
    reportsListQuery.isLoading ||
    (includeSubFundRiskReports && subFundRiskReportsQuery.isLoading);

  const isFetching =
    overviewQuery.isFetching ||
    reportsListQuery.isFetching ||
    (includeSubFundRiskReports && subFundRiskReportsQuery.isFetching);

  return (
    <QueryBoundary<OverviewPageData>
      isLoading={isLoading}
      isFetching={isFetching}
      isError={!!overviewQuery.error}
      error={overviewQuery.error}
      data={pageData}
      loadingMessages={['Loading Overview Data...']}
      errorMessage="No Data Available."
      noDataMessage="No Data Available."
    >
      {({ overview, reports, subFundRiskReports }) => (
        <OverviewTableSection
          overview={overview}
          reports={reports}
          subFundRiskReports={subFundRiskReports}
          clientName={clientName}
          section={props.section}
          sectionId={sectionDetails?.sectionId}
          sectionTitle={sectionDetails?.sectionTitle?.toUpperCase() ?? ''}
          positionDate={props.positionDate}
          getNotesForFund={getNotesForFund}
          preFetchedNotesLoading={preFetchedNotesLoading}
        />
      )}
    </QueryBoundary>
  );
};

export default OverviewPage;
